import React, { useRef, useState } from 'react';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton, cn, useScrollEdges } from '../../../ui';
import DashCard from '../shared/DashCard';
import TileLink from '../shared/TileLink';
import { initialsOf } from '../studentHomeRules';

const MEDIA_TONE_CLASSES = {
  amber: 'tw-bg-warning-soft tw-text-warning',
  emerald: 'tw-bg-success-soft tw-text-success',
  rose: 'tw-bg-danger-soft tw-text-danger',
  sky: 'tw-bg-info-soft tw-text-info',
  violet: 'tw-bg-chart-1-soft tw-text-chart-1',
};

const courseLink = (course) => (course.type === 'Self-Paced' ? `/s/self-paced/${course.id}` : `/s/courses/${course.id}`);

const CourseMedia = ({ course }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className={cn('tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-text-sm tw-font-medium', MEDIA_TONE_CLASSES[course.color] || MEDIA_TONE_CLASSES.violet)}>
      {course.image && !failed
        ? <img src={course.image} alt="" className="tw-h-full tw-w-full tw-object-cover" onError={() => setFailed(true)} />
        : <span aria-hidden="true">{initialsOf(course.title, 'C')}</span>}
    </span>
  );
};

const CourseTile = ({ course }) => (
  <li className="tw-snap-start">
    <TileLink
      to={courseLink(course)}
      className="tw-flex tw-h-full tw-items-center tw-gap-3 tw-rounded tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-3 hover:tw-border-primary/25"
    >
      <CourseMedia course={course} />
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-line-clamp-2 tw-text-[0.8125rem] tw-font-semibold tw-leading-snug">{course.title}</span>
        <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-[0.6875rem] tw-text-content-subtle">
          <BookOpen size={11} aria-hidden="true" />
          {course.type}
        </span>
      </span>
      <ChevronRight size={16} aria-hidden="true" className="tw-shrink-0 tw-text-content-subtle" />
    </TileLink>
  </li>
);

/** Swipes on a touch screen; a mouse gets the arrows, once there is somewhere to go. */
const RecommendedRail = ({ items }) => {
  const railRef = useRef(null);
  const { left, right, onScroll } = useScrollEdges(railRef);

  if (items.length === 0) return null;

  const move = (direction) => {
    const rail = railRef.current;
    rail?.scrollBy?.({ left: direction * rail.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <DashCard
      title="Recommended for You"
      action={(left || right) ? (
        <div className="tw-hidden tw-gap-1.5 [@media(hover:hover)]:tw-flex">
          <IconButton label="Previous courses" icon={ChevronLeft} variant="outline" size="sm" disabled={!left} onClick={() => move(-1)} />
          <IconButton label="More courses" icon={ChevronRight} variant="outline" size="sm" disabled={!right} onClick={() => move(1)} />
        </div>
      ) : null}
    >
      <ul
        ref={railRef}
        onScroll={onScroll}
        className="tw--mx-1 tw-my-0 tw-grid tw-list-none tw-auto-cols-[minmax(240px,1fr)] tw-grid-flow-col tw-gap-3 tw-overflow-x-auto tw-scroll-smooth tw-p-1 [scrollbar-width:none] tw-snap-x tw-snap-mandatory card-md:tw-auto-cols-[minmax(280px,1fr)] motion-reduce:tw-scroll-auto"
      >
        {items.map((course) => <CourseTile key={course.id} course={course} />)}
      </ul>
    </DashCard>
  );
};

export default RecommendedRail;
